import { useEffect, useState } from 'react';
import { handleApiError, showErrorToast } from '../utils/errorHandler';

export function useFormState() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [associateEmail, setAssociateEmail] = useState('');
  const [rg, setRg] = useState('');
  const [cpf, setCpf] = useState('');
  const [matriculation, setMatriculation] = useState('');
  const [regional, setRegional] = useState<string[]>([]);
  const [workplace, setWorkplace] = useState('');
  const [career, setCareer] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [maritalStatusValue, setMaritalStatusValue] = useState<string[]>([]);
  const [mobile, setMobile] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [address, setAddress] = useState('');
  const [stateOptionsValue, setStateOptionsValue] = useState<string[]>([]);
  const [city, setCity] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [number, setNumber] = useState('');
  const [numberOfChildren, setNumberOfChildren] = useState<string | number>('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const isValid = (
      isNomeCompleto(name) &&
      email.trim() !== '' &&
      isValidEmail(email) &&
      rg.trim() !== '' &&
      cpf.trim() !== '' &&
      isValidCPF(cpf) &&
      matriculation.trim() !== '' &&
      regional.length > 0 &&
      workplace.trim() !== '' &&
      career.trim() !== '' &&
      birthDate.trim() !== '' &&
      associateEmail.trim() !== '' &&
      maritalStatusValue.length > 0 &&
      mobile.trim() !== '' &&
      isValidMobile(mobile) &&
      postalCode.trim() !== '' &&
      isValidCEP(postalCode) &&
      stateOptionsValue.length > 0 &&
      address.trim() !== '' &&
      city.trim() !== '' &&
      neighborhood.trim() !== '' &&
      number.trim() !== '' &&
      numberOfChildren !== ''
    );
    setIsFormValid(isValid);
  }, [name, email, associateEmail, rg, cpf, matriculation, regional, workplace, career, birthDate, maritalStatusValue, mobile, postalCode, address, stateOptionsValue, city, neighborhood, number, numberOfChildren]);

  useEffect(() => {
    const newErrors: { [key: string]: string } = {};

    if (!isNomeCompleto(name)) newErrors.name = 'Informe o nome completo.';
    if (!email.trim()) newErrors.email = 'Email é obrigatório.';
    else if (!isValidEmail(email)) newErrors.email = 'Email inválido.';
    if (!associateEmail.trim()) newErrors.associateEmail = 'Email funcional é obrigatório.';
    if (!rg.trim()) newErrors.rg = 'RG é obrigatório.';
    if (!cpf.trim()) newErrors.cpf = 'CPF é obrigatório.';
    else if (!isValidCPF(cpf)) newErrors.cpf = 'CPF inválido.';
    if (!matriculation.trim()) newErrors.matriculation = 'Matrícula é obrigatória.';
    if (regional.length === 0) newErrors.regional = 'Selecione a regional.';
    if (!workplace.trim()) newErrors.workplace = 'Lotação é obrigatória.';
    if (!career.trim()) newErrors.career = 'Carreira é obrigatória.';
    // if (!birthDate.trim()) newErrors.birthDate = 'Data de nascimento é obrigatória.';
    if (!birthDate.trim()) {
      newErrors.birthDate = 'Data de nascimento é obrigatória.';
    } else if (birthDate.length < 10) {
      // Evita validar enquanto o usuário ainda está digitando "20/0..."
      newErrors.birthDate = 'Data incompleta.';
    } else {
      // Só roda a matemática pesada se a data estiver completa
      const dateValidation = validateAndParseDate(birthDate);
      if (!dateValidation.isValid) {
        newErrors.birthDate = dateValidation.error || 'Data inválida.';
      }
    }
    if (maritalStatusValue.length === 0) newErrors.maritalStatus = 'Selecione o estado civil.';
    if (!mobile.trim()) newErrors.mobile = 'Telefone celular é obrigatório.';
    else if (!isValidMobile(mobile)) newErrors.mobile = 'Telefone celular inválido.';
    if (!postalCode.trim()) newErrors.postalCode = 'CEP é obrigatório.';
    else if (!isValidCEP(postalCode)) newErrors.postalCode = 'CEP inválido.';
    if (stateOptionsValue.length === 0) newErrors.state = 'Selecione o estado.';
    if (!address.trim()) newErrors.address = 'Rua é obrigatória.';
    if (!city.trim()) newErrors.city = 'Cidade é obrigatória.';
    if (!neighborhood.trim()) newErrors.neighborhood = 'Bairro é obrigatório.';
    if (!number.trim()) newErrors.number = 'Número é obrigatório.';
    if (numberOfChildren === '') newErrors.numberOfChildren = 'Número de filhos é obrigatório.';

    setErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  }, [
    name, email, associateEmail, rg, cpf, matriculation,
    regional, workplace, career, birthDate, maritalStatusValue,
    mobile, postalCode, address, stateOptionsValue, city, neighborhood, number, numberOfChildren
  ]);
  return {
    name, setName,
    email, setEmail,
    associateEmail, setAssociateEmail,
    rg, setRg,
    cpf, setCpf,
    matriculation, setMatriculation,
    regional, setRegional,
    workplace, setWorkplace,
    career, setCareer,
    birthDate, setBirthDate,
    maritalStatusValue, setMaritalStatusValue,
    mobile, setMobile,
    postalCode, setPostalCode,
    address, setAddress,
    stateOptionsValue, setStateOptionsValue,
    city, setCity,
    neighborhood, setNeighborhood,
    number, setNumber,
    numberOfChildren, setNumberOfChildren,
    isFormValid, errors,
    isSubmitting, isSubmitSuccess, submissionError,
    setIsSubmitting, setIsSubmitSuccess, setSubmissionError, setErrors
  };
}


export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};


export const isValidCPF = (cpf: string): boolean => {
  if (!cpf || cpf.length !== 11 || /^(.)\1+$/.test(cpf)) return false;
  let sum = 0;
  let remainder: number;
  for (let i = 1; i <= 9; i++) sum += parseInt(cpf.substring(i - 1, i), 10) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10), 10)) return false;
  sum = 0;
  for (let i = 1; i <= 10; i++) sum += parseInt(cpf.substring(i - 1, i), 10) * (12 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11), 10)) return false;
  return true;
};

export const isValidMobile = (mobile: string): boolean => {
  // Remove todos os caracteres não numéricos
  const cleanMobile = mobile.replace(/\D/g, '');
  // Verifica se tem 11 dígitos (celular brasileiro)
  return cleanMobile.length === 11;
};

export const isValidCEP = (cep: string): boolean => {
  // Remove todos os caracteres não numéricos
  const cleanCEP = cep.replace(/\D/g, '');
  // Verifica se tem 8 dígitos
  return cleanCEP.length === 8;
};

export const formatCPF = (value: string) => {
  const numeric = value.replace(/\D/g, '').slice(0, 11);
  return numeric
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

export const isNomeCompleto = (nome: string): boolean => {
  const partes = nome.trim().split(/\s+/);
  if (partes.length < 2) return false;
  return partes.every(palavra => palavra.length >= 2);
};

export const useHandleSubmit = (
  isFormValid: boolean,
  formData: any,
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  setIsSubmitSuccess: React.Dispatch<React.SetStateAction<boolean>>,
  setSubmissionError: React.Dispatch<React.SetStateAction<string | null>>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setedigitalApi: any,
  toaster: any,
  router: any
) => {
  return async () => {
    if (!isFormValid) {
      toaster.create({
        title: 'Formulário inválido',
        description: 'Por favor, preencha todos os campos obrigatórios corretamente.',
        type: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const response = await setedigitalApi.post('/associate/create', formData);

      if (response.data.success) {
        setIsSubmitSuccess(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error(response.data.message || 'Erro desconhecido no cadastro');
      }
    } catch (error: any) {
      setIsSubmitting(false);

      // Para erros de validação, não mostramos toast (os erros aparecem nos campos)
      const processedError = handleApiError(error);

      if (
        processedError.code === 'validation_failed' &&
        processedError.details?.fieldErrors
      ) {
        setErrors((prev: Record<string, string>) => {
          const backendErrors = processedError.details.fieldErrors as Record<
            string,
            string[]
          >;

          const mappedErrors: Record<string, string> = {};

          Object.entries(backendErrors).forEach(([field, messages]) => {
            mappedErrors[field] = messages[0];
          });

          return {
            ...prev,
            ...mappedErrors,
          };
        });

        return;
      }


      // if (processedError.code !== 'validation_failed') {
      //   // Mostra toast apenas para erros não relacionados a validação de campos
      //   showErrorToast(processedError, toaster);
      // }

        // setSubmissionError(processedError.message);
      // setSubmissionError(processedError.message);

      // Re-lança o erro para que o componente possa tratá-lo também
      // throw error;
    }
  };
};

export const validateAndParseDate = (dateString: string) => {
  // 1. Regex simples para validar formato visual dd/mm/yyyy
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;

  if (!dateString || !regex.test(dateString)) {
    return { isValid: false, error: "Formato inválido. Use dd/mm/yyyy" };
  }

  // 2. Extração manual (Resolve o problema do Timezone)
  const [dayStr, monthStr, yearStr] = dateString.split('/');
  const day = parseInt(dayStr, 10);
  const month = parseInt(monthStr, 10); // 1-12
  const year = parseInt(yearStr, 10);

  // 3. Validação matemática da data (ex: impede 31/02)
  // O mês no objeto Date começa em 0 (Janeiro = 0)
  const dateObj = new Date(year, month - 1, day);

  // O JS faz "rollover" (31/02 vira 03/03). Verificamos se os componentes batem.
  if (
    dateObj.getFullYear() !== year ||
    dateObj.getMonth() + 1 !== month ||
    dateObj.getDate() !== day
  ) {
    return { isValid: false, error: "Data inexistente." };
  }

  // 4. Regra dos 100 anos
  const today = new Date();

  // Cria uma data limite (exatamente 100 anos atrás do dia de hoje)
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 100);

  // Zera as horas de hoje para comparação justa (apenas data importa)
  today.setHours(0, 0, 0, 0);
  minDate.setHours(0, 0, 0, 0);

  // Verifica se é muito antiga
  if (dateObj < minDate) {
    return { isValid: false, error: "A data não pode ter mais de 100 anos." };
  }

  // Verifica se é data futura
  if (dateObj > today) {
    return { isValid: false, error: "A data não pode ser futura." };
  }

  return { isValid: true, date: dateObj };
};
